import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const BusinessContext = createContext({ businesses: [], active: null, activeId: '', setActive: () => {}, reload: () => {}, loaded: false });
const LS_KEY = 'veluro.activeBusiness';
const SCOPE_KEY = 'veluro.businessScoped';

// One source of truth for "which business am I working as" — persisted per user on the server, cached locally.
export const BusinessProvider = ({ children }) => {
  const { user } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [activeId, setActiveId] = useState(() => { try { return localStorage.getItem(LS_KEY) || ''; } catch { return ''; } });
  const [loaded, setLoaded] = useState(false);
  const [scoped, setScopedState] = useState(() => { try { return localStorage.getItem(SCOPE_KEY) !== '0'; } catch { return true; } });
  const pendingActive = useRef(null); // a switch in flight must not be undone by a slower GET that still carries the old server value
  const setScoped = useCallback((v) => {
    setScopedState(v);
    try { localStorage.setItem(SCOPE_KEY, v ? '1' : '0'); } catch {}
    window.dispatchEvent(new CustomEvent('business-scope-changed', { detail: { scoped: v } }));
  }, []);

  const reload = useCallback(async () => {
    if (!user) return [];
    try {
      const r = await api.get('/business-profiles');
      const list = r.data.brands || [];
      setBusinesses(list);
      const srv = r.data.active_profile_id || '';
      if (srv && !pendingActive.current) { setActiveId(srv); try { localStorage.setItem(LS_KEY, srv); } catch {} }
      return list;
    } catch { return []; } finally { setLoaded(true); }
  }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    const h = () => reload();
    window.addEventListener('business-profiles-changed', h);
    return () => window.removeEventListener('business-profiles-changed', h);
  }, [reload]);

  const setActive = useCallback(async (pid) => {
    pendingActive.current = pid;
    setActiveId(pid);
    try { localStorage.setItem(LS_KEY, pid); } catch {}
    try { await api.put('/business-profiles/active', { profile_id: pid }); } catch {}
    finally { if (pendingActive.current === pid) pendingActive.current = null; }
    window.dispatchEvent(new CustomEvent('business-changed', { detail: { profile_id: pid } }));
  }, []);

  const active = useMemo(() => businesses.find((b) => b.profile_id === activeId) || businesses.find((b) => b.is_default) || businesses[0] || null, [businesses, activeId]);
  useEffect(() => { if (active?.profile_id) { try { localStorage.setItem(LS_KEY, active.profile_id); } catch {} } }, [active?.profile_id]);
  const value = useMemo(() => ({ businesses, active, activeId: active?.profile_id || '', setActive, reload, loaded, scoped: scoped && businesses.length > 1, setScoped }),
    [businesses, active, setActive, reload, loaded, scoped, setScoped]);
  return <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>;
};

export const useBusiness = () => useContext(BusinessContext);
